import { capitalizeLeadingCharacter } from "./capitalizeLeadingCharacter";
import {
  countryFilterSelectValues,
  expandSignupSupportedCountryNames,
} from "./profileHeadquartersCountries";
import { getCountryDisplayLabelFromHeadquarters } from "./worldCountryIsoCodes";

export const ECOSYSTEM_HEAD_QUESTION_TYPE_OPTIONS = [
  { value: "short_text", label: "Short answer" },
  { value: "paragraph", label: "Paragraph" },
  { value: "multiple_choice", label: "Multiple choice" },
  { value: "checkboxes", label: "Checkboxes" },
  { value: "document_upload", label: "Document upload" },
  { value: "description", label: "Description" },
] as const;

export type EcosystemHeadQuestionType = (typeof ECOSYSTEM_HEAD_QUESTION_TYPE_OPTIONS)[number]["value"];

/** Language-side id used as the active wording copy. First language is always `"a"`. */
export type EcosystemHeadQuestionnaireLanguageId = string;

export type EcosystemHeadQuestionnaireLanguage = {
  id: string;
  name: string;
};

export type EcosystemHeadQuestionnaireLanguages = {
  languages: EcosystemHeadQuestionnaireLanguage[];
};

export const DEFAULT_QUESTIONNAIRE_LANGUAGE_LIMIT = 2;
export const ABSOLUTE_MAX_QUESTIONNAIRE_LANGUAGES = 12;
/** @deprecated Use ABSOLUTE_MAX_QUESTIONNAIRE_LANGUAGES */
export const MAX_QUESTIONNAIRE_LANGUAGES = ABSOLUTE_MAX_QUESTIONNAIRE_LANGUAGES;

export function clampQuestionnaireMaxLanguages(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_QUESTIONNAIRE_LANGUAGE_LIMIT;
  return Math.min(
    ABSOLUTE_MAX_QUESTIONNAIRE_LANGUAGES,
    Math.max(DEFAULT_QUESTIONNAIRE_LANGUAGE_LIMIT, Math.trunc(n)),
  );
}

export function assertQuestionnaireLanguageCount(
  languages: EcosystemHeadQuestionnaireLanguages | null | undefined,
  maxLanguages: number,
): void {
  const count = languages?.languages.length ?? 0;
  const max = clampQuestionnaireMaxLanguages(maxLanguages);
  if (count > max) {
    throw new Error(`This questionnaire can have at most ${max} languages.`);
  }
}

export type EcosystemHeadQuestionOption = {
  id: string;
  label: string;
  /** Legacy second-language copy of the option text. */
  labelB?: string;
  /** Extra language copies keyed by language-side id (not including the first language). */
  labels?: Record<string, string>;
  /** When true, selecting this option indicates a good fit. Used for screening on review. */
  expected?: boolean;
  /** When true (or label is "Other"), selecting this option requires a free-text specify field. */
  allowsOtherText?: boolean;
};

/** Answer map key for the free-text that accompanies an "Other" choice. */
export function otherTextAnswerKey(questionId: string): string {
  return `${questionId}__other`;
}

export function isOtherTextAnswerKey(key: string): boolean {
  return key.endsWith("__other");
}

function isOtherOptionLabel(value: string | undefined): boolean {
  return (value ?? "").trim().toLowerCase() === "other";
}

function optionLabelVariants(
  option: Pick<EcosystemHeadQuestionOption, "label" | "labelB" | "labels">,
): string[] {
  return [option.label, option.labelB, ...Object.values(option.labels ?? {})].filter(
    (value): value is string => typeof value === "string",
  );
}

export function optionAllowsOtherText(
  option: Pick<EcosystemHeadQuestionOption, "label" | "labelB" | "labels" | "allowsOtherText">,
): boolean {
  if (option.allowsOtherText === true) return true;
  return optionLabelVariants(option).some(isOtherOptionLabel);
}

/** Standalone "Other: Please specify" questions that were mistakenly added as numbered items. */
export function isLegacyOtherSpecifyQuestion(
  question: Pick<EcosystemHeadQuestion, "id" | "label" | "labelB" | "labels" | "type">,
): boolean {
  if (question.id === "enq-q1-other") return true;
  if (question.type !== "short_text" && question.type !== "paragraph") return false;
  const legacy = /^other:\s*please specify\.?$/i;
  const variants = [question.label, question.labelB, ...Object.values(question.labels ?? {})];
  return variants.some((value) => typeof value === "string" && legacy.test(value.trim()));
}

export function stripLegacyOtherSpecifyQuestions<T extends Pick<EcosystemHeadQuestion, "id" | "label" | "type">>(
  questions: T[],
): T[] {
  return questions.filter((question) => !isLegacyOtherSpecifyQuestion(question));
}

export function getSelectedOtherOption(
  question: Pick<EcosystemHeadQuestion, "type" | "options">,
  raw: string | undefined,
): EcosystemHeadQuestionOption | undefined {
  if (!isChoiceQuestionType(question.type)) return undefined;
  const options = question.options ?? [];
  const otherOptions = options.filter(optionAllowsOtherText);
  if (otherOptions.length === 0) return undefined;

  const selectedIds =
    question.type === "checkboxes"
      ? parseCheckboxAnswer(raw)
      : raw?.trim()
        ? [raw.trim()]
        : [];

  return otherOptions.find((option) => selectedIds.includes(option.id));
}

export type EcosystemHeadQuestion = {
  id: string;
  label: string;
  /** Legacy second-language copy of the question or description text. */
  labelB?: string;
  /** Extra language copies keyed by language-side id (not including the first language). */
  labels?: Record<string, string>;
  type: EcosystemHeadQuestionType;
  required: boolean;
  options?: EcosystemHeadQuestionOption[];
  /** First-class builder section this question belongs to. */
  sectionId?: string;
};

export type EcosystemHeadQuestionInput = {
  id?: string;
  label: string;
  labelB?: string;
  labels?: Record<string, string>;
  type?: EcosystemHeadQuestionType;
  required?: boolean;
  sectionId?: string;
  options?: Array<{
    id?: string;
    label: string;
    labelB?: string;
    labels?: Record<string, string>;
    expected?: boolean;
  }>;
};

export type EcosystemHeadSection = {
  id: string;
  title: string;
  titleB?: string;
  titles?: Record<string, string>;
};

export type EcosystemHeadSectionInput = {
  id?: string;
  title: string;
  titleB?: string;
  titles?: Record<string, string>;
};

export type EcosystemHeadQuestionnaireSnapshotParse = {
  questionsRaw: unknown;
  sectionsRaw: unknown;
};

export function parseEcosystemHeadQuestionnaireSnapshot(
  raw: unknown,
): EcosystemHeadQuestionnaireSnapshotParse {
  if (Array.isArray(raw)) {
    return { questionsRaw: raw, sectionsRaw: null };
  }
  if (raw && typeof raw === "object") {
    const row = raw as Record<string, unknown>;
    if (row.forms && typeof row.forms === "object" && !Array.isArray(row.forms)) {
      const forms = row.forms as Record<string, unknown>;
      const first = Object.values(forms)[0];
      if (first && typeof first === "object" && !Array.isArray(first)) {
        const form = first as Record<string, unknown>;
        return {
          questionsRaw: Array.isArray(form.questions) ? form.questions : [],
          sectionsRaw: form.sections ?? null,
        };
      }
    }
    if (Array.isArray(row.questions)) {
      return { questionsRaw: row.questions, sectionsRaw: row.sections ?? null };
    }
  }
  return { questionsRaw: [], sectionsRaw: null };
}

export function serializeEcosystemHeadQuestionnaireSnapshot(
  questions: EcosystemHeadQuestion[],
  sections: EcosystemHeadSection[],
): { questions: EcosystemHeadQuestion[]; sections: EcosystemHeadSection[] } {
  return { questions, sections };
}

export const ECOSYSTEM_HEAD_QUESTIONNAIRE_FORMS_VERSION = 2;

export type EcosystemHeadQuestionnaireForm = {
  questions: EcosystemHeadQuestion[];
  sections: EcosystemHeadSection[];
};

export function emptyQuestionnaireForm(): EcosystemHeadQuestionnaireForm {
  return { questions: [], sections: [] };
}

function parseQuestionnaireFormEntry(raw: unknown): EcosystemHeadQuestionnaireForm {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return emptyQuestionnaireForm();
  const row = raw as Record<string, unknown>;
  return {
    questions: Array.isArray(row.questions) ? (row.questions as EcosystemHeadQuestion[]) : [],
    sections: parseEcosystemHeadSections(row.sections),
  };
}

export function serializeEcosystemHeadQuestionnaireForms(
  forms: Record<string, EcosystemHeadQuestionnaireForm>,
): { version: number; forms: Record<string, EcosystemHeadQuestionnaireForm> } {
  return { version: ECOSYSTEM_HEAD_QUESTIONNAIRE_FORMS_VERSION, forms };
}

export function parseEcosystemHeadQuestionnaireForms(
  questionsJson: unknown,
  sectionsJson?: unknown,
  languageIds: string[] = ["a"],
): Record<string, EcosystemHeadQuestionnaireForm> {
  const ids = languageIds.length > 0 ? languageIds : ["a"];
  const firstId = ids[0] ?? "a";
  let parsed: Record<string, EcosystemHeadQuestionnaireForm> | null = null;

  if (questionsJson && typeof questionsJson === "object" && !Array.isArray(questionsJson)) {
    const row = questionsJson as Record<string, unknown>;
    if (row.forms && typeof row.forms === "object" && !Array.isArray(row.forms)) {
      parsed = {};
      for (const [id, form] of Object.entries(row.forms as Record<string, unknown>)) {
        parsed[id] = parseQuestionnaireFormEntry(form);
      }
    } else if (Array.isArray(row.questions)) {
      parsed = {
        [firstId]: {
          questions: row.questions as EcosystemHeadQuestion[],
          sections: parseEcosystemHeadSections(row.sections ?? sectionsJson),
        },
      };
    }
  } else if (Array.isArray(questionsJson)) {
    parsed = {
      [firstId]: {
        questions: questionsJson as EcosystemHeadQuestion[],
        sections: parseEcosystemHeadSections(sectionsJson),
      },
    };
  }

  const result: Record<string, EcosystemHeadQuestionnaireForm> = {};
  for (const id of ids) {
    result[id] = parsed?.[id] ?? emptyQuestionnaireForm();
  }
  return result;
}

export function pickQuestionnaireFormForHeadquarters(
  forms: Record<string, EcosystemHeadQuestionnaireForm>,
  languages: EcosystemHeadQuestionnaireLanguages | null | undefined,
  headquartersCountry?: string | null,
): EcosystemHeadQuestionnaireForm {
  return resolveQuestionnaireFillTarget(forms, languages, headquartersCountry).form;
}

export function resolveQuestionnaireFillTarget(
  forms: Record<string, EcosystemHeadQuestionnaireForm>,
  languages: EcosystemHeadQuestionnaireLanguages | null | undefined,
  headquartersCountry?: string | null,
  requestedLanguageId?: string | null,
): { languageId: string; form: EcosystemHeadQuestionnaireForm } {
  const list = languages?.languages ?? [];
  const firstId = list[0]?.id ?? Object.keys(forms)[0] ?? "a";
  const requested = requestedLanguageId?.trim();
  if (requested && forms[requested] && (list.length === 0 || list.some((language) => language.id === requested))) {
    return { languageId: requested, form: forms[requested] ?? emptyQuestionnaireForm() };
  }
  const names = list.map((language) => language.name);
  const matchedName =
    matchSupportedSignupCountry(names, headquartersCountry) ??
    matchSupportedSignupCountry(names, getCountryDisplayLabelFromHeadquarters(headquartersCountry));
  const matched = matchedName
    ? list.find((language) => language.name.toLowerCase() === matchedName.toLowerCase())
    : undefined;
  const id = matched?.id ?? firstId;
  return { languageId: id, form: forms[id] ?? forms[firstId] ?? emptyQuestionnaireForm() };
}

export function activeFillQuestionnaireForm(
  forms: Record<string, EcosystemHeadQuestionnaireForm> | null | undefined,
  languageId: string | null | undefined,
  fallback: EcosystemHeadQuestionnaireForm,
): EcosystemHeadQuestionnaireForm {
  const id = languageId?.trim();
  if (id && forms?.[id]) return forms[id];
  return fallback;
}

export function parseEcosystemHeadSections(raw: unknown): EcosystemHeadSection[] {
  if (!Array.isArray(raw)) return [];
  const sections: EcosystemHeadSection[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const title = typeof row.title === "string" ? row.title.trim() : "";
    if (!title) continue;
    const id = typeof row.id === "string" && row.id.trim() ? row.id.trim() : `sec-${sections.length + 1}`;
    const titles = normalizeQuestionnaireLabelMap(row.titles);
    const titleB = typeof row.titleB === "string" ? row.titleB.trim() : undefined;
    sections.push({
      id,
      title,
      ...(titleB ? { titleB } : {}),
      ...(titles ? { titles } : {}),
    });
  }
  return sections;
}

export function normalizeQuestionnaireLabelMap(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const labels: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const id = key.trim();
    const text = typeof value === "string" ? value.trim() : "";
    if (!id || !text) continue;
    labels[id] = text;
  }
  return Object.keys(labels).length > 0 ? labels : undefined;
}

function normalizeLanguageList(raw: unknown[]): EcosystemHeadQuestionnaireLanguage[] {
  const languages: EcosystemHeadQuestionnaireLanguage[] = [];
  const seen = new Set<string>();
  raw.forEach((item, index) => {
    if (languages.length >= ABSOLUTE_MAX_QUESTIONNAIRE_LANGUAGES) return;
    if (typeof item === "string") {
      const name = item.trim();
      if (!name) return;
      const id = index === 0 ? "a" : index === 1 ? "b" : `c${index + 1}`;
      if (seen.has(id)) return;
      seen.add(id);
      languages.push({ id, name });
      return;
    }
    if (!item || typeof item !== "object") return;
    const row = item as Record<string, unknown>;
    const name = typeof row.name === "string" ? row.name.trim() : "";
    if (!name) return;
    const rawId = typeof row.id === "string" ? row.id.trim() : "";
    const id = rawId || (index === 0 ? "a" : index === 1 ? "b" : `c${index + 1}`);
    if (seen.has(id)) return;
    seen.add(id);
    languages.push({ id, name });
  });
  return languages;
}

/** Split stored corridor countries (comma / semicolon / newline). */
export function splitCorridorCountryList(raw?: string | null): string[] {
  if (!raw?.trim()) return [];
  const seen = new Set<string>();
  const names: string[] = [];
  for (const part of raw.split(/[,;\n]+/)) {
    const name = part.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
}

export function joinCorridorCountryList(names: string[]): string {
  return splitCorridorCountryList(names.join(", ")).join(", ");
}

export function languagesFromCorridorProfile(
  country?: string | null,
  targetCountry?: string | null,
): EcosystemHeadQuestionnaireLanguage[] {
  const head = country?.trim() ?? "";
  const target = splitCorridorCountryList(targetCountry)[0] ?? "";
  const languages: EcosystemHeadQuestionnaireLanguage[] = [];
  if (head) languages.push({ id: "a", name: head });
  if (target && target.toLowerCase() !== head.toLowerCase()) {
    languages.push({ id: "b", name: target });
  }
  if (languages.length === 0) languages.push({ id: "a", name: "" });
  return languages;
}

function corridorCountryNames(
  profileCountry?: string | null,
  profileTargetCountry?: string | null,
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const raw of [profileCountry, ...splitCorridorCountryList(profileTargetCountry)]) {
    const name = raw?.trim() ?? "";
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
}

/** HQ countries companies may pick on corridor signup: cluster head country + all target countries. */
export function supportedSignupCountryNames(
  profileCountry?: string | null,
  profileTargetCountry?: string | null,
): string[] {
  return expandSignupSupportedCountryNames(corridorCountryNames(profileCountry, profileTargetCountry));
}

/** Within-cluster country filter: continent labels plus catalog countries. */
export function supportedCountryFilterSelectValues(
  profileCountry?: string | null,
  profileTargetCountry?: string | null,
): string[] {
  return countryFilterSelectValues(corridorCountryNames(profileCountry, profileTargetCountry));
}

export function matchSupportedSignupCountry(
  allowed: string[],
  input?: string | null,
): string | null {
  const needle = input?.trim().toLowerCase() ?? "";
  if (!needle) return null;
  return allowed.find((name) => name.toLowerCase() === needle) ?? null;
}

/** Prefer the cluster-head country when it is in the corridor signup list. */
export function defaultCorridorSignupCountry(
  allowed: string[],
  clusterHeadCountry?: string | null,
): string | null {
  const direct = matchSupportedSignupCountry(allowed, clusterHeadCountry);
  if (direct) return direct;
  return matchSupportedSignupCountry(
    allowed,
    getCountryDisplayLabelFromHeadquarters(clusterHeadCountry),
  );
}

export function parseEcosystemHeadQuestionnaireLanguages(
  raw: unknown,
): EcosystemHeadQuestionnaireLanguages | null {
  if (!raw || typeof raw !== "object") return null;

  if (Array.isArray(raw)) {
    const languages = normalizeLanguageList(raw);
    return languages.length >= 1 ? { languages } : null;
  }

  const row = raw as Record<string, unknown>;
  const listRaw = Array.isArray(row.languages)
    ? row.languages
    : Array.isArray(row.countries)
      ? row.countries
      : null;
  if (listRaw) {
    const languages = normalizeLanguageList(listRaw);
    return languages.length >= 1 ? { languages } : null;
  }

  const a = typeof row.a === "string" ? row.a.trim() : "";
  const b = typeof row.b === "string" ? row.b.trim() : "";
  const languages: EcosystemHeadQuestionnaireLanguage[] = [];
  if (a) languages.push({ id: "a", name: a });
  if (b && b.toLowerCase() !== a.toLowerCase()) languages.push({ id: "b", name: b });
  return languages.length >= 1 ? { languages } : null;
}

export function serializeEcosystemHeadQuestionnaireLanguagesJson(
  languages: EcosystemHeadQuestionnaireLanguages | null | undefined,
): { languages: EcosystemHeadQuestionnaireLanguage[] } | null {
  if (!languages?.languages.length) return null;
  return { languages: languages.languages };
}

export function extraQuestionnaireLanguageIds(
  languages: EcosystemHeadQuestionnaireLanguages | null | undefined,
): string[] {
  return (languages?.languages ?? []).slice(1).map((language) => language.id);
}

function questionHasExtraCopy(question: EcosystemHeadQuestion): boolean {
  if (question.labelB?.trim()) return true;
  if (Object.values(question.labels ?? {}).some((value) => value.trim())) return true;
  return (question.options ?? []).some(
    (option) =>
      Boolean(option.labelB?.trim()) ||
      Object.values(option.labels ?? {}).some((value) => value.trim()),
  );
}

export function hasBilingualCopy(questions: EcosystemHeadQuestion[]): boolean {
  return questions.some(questionHasExtraCopy);
}

export function resolveQuestionnaireLanguagesForDisplay(
  stored: unknown,
  questions: EcosystemHeadQuestion[],
): EcosystemHeadQuestionnaireLanguages | null {
  if (!hasBilingualCopy(questions)) return null;
  return (
    parseEcosystemHeadQuestionnaireLanguages(stored) ?? {
      languages: [
        { id: "a", name: "Language 1" },
        { id: "b", name: "Language 2" },
      ],
    }
  );
}

export function shouldShowQuestionnaireLanguageToggle(
  languages: EcosystemHeadQuestionnaireLanguages | null | undefined,
  questions: EcosystemHeadQuestion[],
  forms?: Record<string, EcosystemHeadQuestionnaireForm> | null,
): boolean {
  if ((languages?.languages.length ?? 0) < 2) return false;
  if (forms) {
    const readyLanguages = languages!.languages.filter((language) =>
      (forms[language.id]?.questions ?? []).some(isAnswerableEcosystemHeadQuestion),
    );
    return readyLanguages.length >= 2;
  }
  return hasBilingualCopy(questions);
}

function textForLanguage(
  languageId: string,
  firstLanguageId: string,
  primary: string,
  labelB: string | undefined,
  labels: Record<string, string> | undefined,
): string {
  if (!languageId || languageId === firstLanguageId) return primary;
  const fromMap = labels?.[languageId]?.trim();
  if (fromMap) return fromMap;
  if (languageId === "b") return labelB?.trim() || primary;
  return primary;
}

export function localizeEcosystemHeadQuestion(
  question: EcosystemHeadQuestion,
  side: EcosystemHeadQuestionnaireLanguageId,
  languages?: EcosystemHeadQuestionnaireLanguages | null,
): EcosystemHeadQuestion {
  const firstLanguageId = languages?.languages[0]?.id ?? "a";
  if (!side || side === firstLanguageId) return question;
  return {
    ...question,
    label: textForLanguage(side, firstLanguageId, question.label, question.labelB, question.labels),
    options: question.options?.map((option) => ({
      ...option,
      label: textForLanguage(side, firstLanguageId, option.label, option.labelB, option.labels),
    })),
  };
}

export function localizeEcosystemHeadQuestions(
  questions: EcosystemHeadQuestion[],
  side: EcosystemHeadQuestionnaireLanguageId,
  languages?: EcosystemHeadQuestionnaireLanguages | null,
): EcosystemHeadQuestion[] {
  const firstLanguageId = languages?.languages[0]?.id ?? "a";
  if (!side || side === firstLanguageId) return questions;
  return questions.map((question) => localizeEcosystemHeadQuestion(question, side, languages));
}

const CHOICE_TYPES = new Set<EcosystemHeadQuestionType>(["multiple_choice", "checkboxes"]);
const ANSWERABLE_TYPES = new Set<EcosystemHeadQuestionType>([
  "short_text",
  "paragraph",
  "multiple_choice",
  "checkboxes",
  "document_upload",
]);

export type EcosystemHeadDocumentUploadAnswer = {
  fileId?: string;
  fileName?: string;
  explanation?: string;
};

export function isChoiceQuestionType(type: EcosystemHeadQuestionType): boolean {
  return CHOICE_TYPES.has(type);
}

export function isAnswerableEcosystemHeadQuestion(question: Pick<EcosystemHeadQuestion, "type">): boolean {
  return ANSWERABLE_TYPES.has(question.type);
}

export function normalizeEcosystemHeadQuestionType(raw: unknown): EcosystemHeadQuestionType {
  if (typeof raw === "string" && ANSWERABLE_TYPES.has(raw as EcosystemHeadQuestionType)) {
    return raw as EcosystemHeadQuestionType;
  }
  if (raw === "description") return "description";
  return "paragraph";
}

export function defaultEcosystemHeadQuestionOptions(): EcosystemHeadQuestionOption[] {
  return [
    { id: "opt-1", label: "Option 1" },
    { id: "opt-2", label: "Option 2" },
  ];
}

export function parseDocumentUploadAnswer(raw: string | undefined): EcosystemHeadDocumentUploadAnswer {
  if (!raw?.trim()) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const row = parsed as Record<string, unknown>;
    return {
      ...(typeof row.fileId === "string" && row.fileId.trim() ? { fileId: row.fileId.trim() } : {}),
      ...(typeof row.fileName === "string" && row.fileName.trim() ? { fileName: row.fileName.trim() } : {}),
      ...(typeof row.explanation === "string" && row.explanation.trim()
        ? { explanation: row.explanation.trim() }
        : {}),
    };
  } catch {
    return {};
  }
}

export function serializeDocumentUploadAnswer(answer: EcosystemHeadDocumentUploadAnswer): string {
  return JSON.stringify({
    ...(answer.fileId ? { fileId: answer.fileId } : {}),
    ...(answer.fileName ? { fileName: answer.fileName } : {}),
    ...(answer.explanation ? { explanation: answer.explanation } : {}),
  });
}

export function isDocumentUploadAnswerComplete(
  required: boolean,
  answer: EcosystemHeadDocumentUploadAnswer,
): boolean {
  const hasFile = Boolean(answer.fileId?.trim());
  const hasExplanation = Boolean(answer.explanation?.trim());
  if (!required) return true;
  return hasFile || hasExplanation;
}

export function parseCheckboxAnswer(raw: string | undefined): string[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  } catch {
    return [];
  }
}

export function appendAnsweredQuestionStubs(
  questions: EcosystemHeadQuestion[],
  answers: Record<string, string>,
): EcosystemHeadQuestion[] {
  const knownIds = new Set(questions.map((question) => question.id));
  const extras: EcosystemHeadQuestion[] = [];

  for (const [id, value] of Object.entries(answers)) {
    if (knownIds.has(id) || !value?.trim() || isOtherTextAnswerKey(id)) continue;
    extras.push({
      id,
      label: "Previous question (removed from form)",
      type: "paragraph",
      required: false,
    });
  }

  return [...questions, ...extras];
}

export type EcosystemHeadDocumentAnswer = {
  questionId: string;
  label: string;
  fileId: string;
  fileName?: string;
};

export type EcosystemHeadQuestionnaireSection = {
  id: string;
  title: string;
  questions: EcosystemHeadQuestion[];
};

export function isEcosystemHeadQuestionAnswered(
  question: EcosystemHeadQuestion,
  raw: string | undefined,
): boolean {
  if (!isAnswerableEcosystemHeadQuestion(question)) return false;

  if (question.type === "checkboxes") {
    return parseCheckboxAnswer(raw).length > 0;
  }

  if (question.type === "document_upload") {
    const parsed = parseDocumentUploadAnswer(raw);
    return Boolean(parsed.fileId?.trim() || parsed.explanation?.trim());
  }

  return Boolean(raw?.trim());
}

export function isEcosystemHeadQuestionAnswerComplete(
  question: EcosystemHeadQuestion,
  raw: string | undefined,
  answers?: Record<string, string>,
): boolean {
  if (!isAnswerableEcosystemHeadQuestion(question)) return true;

  if (question.type === "checkboxes") {
    if (question.required && parseCheckboxAnswer(raw).length === 0) return false;
    if (!getSelectedOtherOption(question, raw)) return true;
    const otherText = answers?.[otherTextAnswerKey(question.id)]?.trim() ?? "";
    return Boolean(otherText);
  }

  if (question.type === "multiple_choice") {
    if (question.required && !raw?.trim()) return false;
    if (!getSelectedOtherOption(question, raw)) return true;
    const otherText = answers?.[otherTextAnswerKey(question.id)]?.trim() ?? "";
    return Boolean(otherText);
  }

  if (question.type === "document_upload") {
    return isDocumentUploadAnswerComplete(question.required, parseDocumentUploadAnswer(raw));
  }

  if (question.required) return Boolean(raw?.trim());
  return true;
}

export function getEcosystemHeadQuestionnaireSubmitState(
  questions: EcosystemHeadQuestion[],
  answers: Record<string, string>,
): { canSubmit: boolean; remaining: number } {
  const answerable = questions.filter(isAnswerableEcosystemHeadQuestion);
  const incomplete = answerable.filter(
    (question) => !isEcosystemHeadQuestionAnswerComplete(question, answers[question.id], answers),
  );
  return {
    canSubmit: answerable.length > 0 && incomplete.length === 0,
    remaining: incomplete.length,
  };
}

export function computeEcosystemHeadQuestionnaireCompletion(
  questions: EcosystemHeadQuestion[],
  answers: Record<string, string>,
): number {
  const answerable = questions.filter(isAnswerableEcosystemHeadQuestion);
  if (answerable.length === 0) return 0;

  const completed = answerable.filter((question) =>
    isEcosystemHeadQuestionAnswerComplete(question, answers[question.id], answers),
  ).length;

  return Math.round((completed / answerable.length) * 100);
}

export function extractEcosystemHeadDocumentAnswers(
  questions: EcosystemHeadQuestion[],
  answers: Record<string, string>,
): EcosystemHeadDocumentAnswer[] {
  const documents: EcosystemHeadDocumentAnswer[] = [];

  for (const question of questions) {
    if (question.type !== "document_upload") continue;
    const parsed = parseDocumentUploadAnswer(answers[question.id]);
    if (!parsed.fileId?.trim()) continue;
    documents.push({
      questionId: question.id,
      label: question.label,
      fileId: parsed.fileId,
      ...(parsed.fileName ? { fileName: parsed.fileName } : {}),
    });
  }

  return documents;
}

export function localizeEcosystemHeadSection(
  section: EcosystemHeadSection,
  side: EcosystemHeadQuestionnaireLanguageId,
  languages?: EcosystemHeadQuestionnaireLanguages | null,
): EcosystemHeadSection {
  const firstLanguageId = languages?.languages[0]?.id ?? "a";
  if (!side || side === firstLanguageId) return section;
  return {
    ...section,
    title: textForLanguage(side, firstLanguageId, section.title, section.titleB, section.titles),
  };
}

export function localizeEcosystemHeadSections(
  sections: EcosystemHeadSection[],
  side: EcosystemHeadQuestionnaireLanguageId,
  languages?: EcosystemHeadQuestionnaireLanguages | null,
): EcosystemHeadSection[] {
  const firstLanguageId = languages?.languages[0]?.id ?? "a";
  if (!side || side === firstLanguageId) return sections;
  return sections.map((section) => localizeEcosystemHeadSection(section, side, languages));
}

export function groupEcosystemHeadQuestionsIntoSections(
  questions: EcosystemHeadQuestion[],
  sectionDefs?: EcosystemHeadSection[] | null,
): EcosystemHeadQuestionnaireSection[] {
  if (sectionDefs && sectionDefs.length > 0) {
    const grouped: EcosystemHeadQuestionnaireSection[] = [];
    const bySectionId = new Map<string, EcosystemHeadQuestion[]>();
    const unmatched: EcosystemHeadQuestion[] = [];

    for (const question of questions) {
      const sectionId = question.sectionId?.trim();
      if (!sectionId || !sectionDefs.some((section) => section.id === sectionId)) {
        unmatched.push(question);
        continue;
      }
      const existing = bySectionId.get(sectionId) ?? [];
      existing.push(question);
      bySectionId.set(sectionId, existing);
    }

    if (unmatched.length > 0) {
      grouped.push({
        id: "default-section",
        title: "Questionnaire",
        questions: unmatched,
      });
    }

    for (const section of sectionDefs) {
      const sectionQuestions = bySectionId.get(section.id) ?? [];
      if (sectionQuestions.length === 0) continue;
      grouped.push({
        id: section.id,
        title: section.title.trim() || "Untitled section",
        questions: sectionQuestions,
      });
    }

    return grouped.length > 0
      ? grouped
      : [{ id: "default-section", title: "Questionnaire", questions: [...questions] }];
  }

  const sections: EcosystemHeadQuestionnaireSection[] = [];
  let current: EcosystemHeadQuestionnaireSection | null = null;

  for (const question of questions) {
    if (question.type === "description") {
      current = {
        id: question.id,
        title: question.label.trim() || "Section",
        questions: [],
      };
      sections.push(current);
      continue;
    }

    if (!current) {
      current = {
        id: "default-section",
        title: "Questionnaire",
        questions: [],
      };
      sections.push(current);
    }

    current.questions.push(question);
  }

  if (sections.length === 0) {
    return [{ id: "default-section", title: "Questionnaire", questions: [...questions] }];
  }

  return sections;
}

export function splitQuestionnaireSectionAfterQuestion<
  Q extends { sectionClientId?: string },
  S extends { clientId: string },
>(
  draft: { questions: Q[]; sections: S[] },
  activeIndex: number,
  createSection: () => S,
): { questions: Q[]; sections: S[] } {
  const questions = draft.questions.map((question) => ({ ...question }));
  const clampedIndex = Math.max(0, Math.min(activeIndex, Math.max(questions.length - 1, 0)));

  if (draft.sections.length === 0) {
    const first = createSection();
    const second = createSection();
    const nextQuestions = questions.map((question, index) => ({
      ...question,
      sectionClientId: index <= clampedIndex ? first.clientId : second.clientId,
    }));
    return { questions: nextQuestions, sections: [first, second] };
  }

  const active = questions[clampedIndex];
  const activeSectionId = active?.sectionClientId ?? draft.sections[0]?.clientId;
  const sectionIndex = draft.sections.findIndex((section) => section.clientId === activeSectionId);
  const insertAt = sectionIndex >= 0 ? sectionIndex + 1 : draft.sections.length;
  const created = createSection();
  const nextSections = [...draft.sections];
  nextSections.splice(insertAt, 0, created);

  const nextQuestions = questions.map((question, index) => {
    if (index <= clampedIndex) return question;
    if (question.sectionClientId !== activeSectionId) return question;
    return { ...question, sectionClientId: created.clientId };
  });

  return { questions: nextQuestions, sections: nextSections };
}

export function deleteQuestionnaireSectionMovingQuestions<
  Q extends { sectionClientId?: string },
  S extends { clientId: string },
>(
  draft: { questions: Q[]; sections: S[] },
  sectionClientId: string,
): { questions: Q[]; sections: S[] } | null {
  if (draft.sections.length <= 1) return null;
  const sectionIndex = draft.sections.findIndex((section) => section.clientId === sectionClientId);
  if (sectionIndex < 0) return null;
  const destination = draft.sections[sectionIndex - 1] ?? draft.sections[sectionIndex + 1];
  if (!destination) return null;

  return {
    sections: draft.sections.filter((section) => section.clientId !== sectionClientId),
    questions: draft.questions.map((question) =>
      question.sectionClientId === sectionClientId
        ? { ...question, sectionClientId: destination.clientId }
        : question,
    ),
  };
}

export function hasExpectedAnswerScreening(
  question: Pick<EcosystemHeadQuestion, "type" | "options">,
): boolean {
  if (!isChoiceQuestionType(question.type)) return false;
  return (question.options ?? []).some((option) => option.expected === true);
}

export type ChoiceAnswerScreeningItem = {
  optionId: string;
  label: string;
  isExpected: boolean;
};

export function getSelectedChoiceAnswerItems(
  question: Pick<EcosystemHeadQuestion, "type" | "options">,
  raw: string | undefined,
): ChoiceAnswerScreeningItem[] {
  if (!isChoiceQuestionType(question.type)) return [];

  const options = question.options ?? [];
  const selectedIds =
    question.type === "checkboxes"
      ? parseCheckboxAnswer(raw)
      : raw?.trim()
        ? [raw.trim()]
        : [];

  return selectedIds.map((optionId) => {
    const option = options.find((row) => row.id === optionId);
    return {
      optionId,
      label: option?.label ?? optionId,
      isExpected: option?.expected === true,
    };
  });
}

export function hasWrongChoiceSelections(
  question: Pick<EcosystemHeadQuestion, "type" | "options">,
  raw: string | undefined,
): boolean {
  if (!hasExpectedAnswerScreening(question)) return false;
  return getSelectedChoiceAnswerItems(question, raw).some((item) => !item.isExpected);
}

export function countWrongChoiceSelections(
  questions: EcosystemHeadQuestion[],
  answers: Record<string, string>,
): number {
  let count = 0;
  for (const question of questions) {
    if (!hasExpectedAnswerScreening(question)) continue;
    count += getSelectedChoiceAnswerItems(question, answers[question.id]).filter(
      (item) => !item.isExpected,
    ).length;
  }
  return count;
}

function formatChoiceOptionLabel(
  option: EcosystemHeadQuestionOption | undefined,
  optionId: string,
  otherText?: string,
): string {
  if (!option) return optionId;
  if (optionAllowsOtherText(option) && otherText?.trim()) {
    return `Other: ${otherText.trim()}`;
  }
  return option.label;
}

export function formatEcosystemHeadAnswer(
  question: Pick<EcosystemHeadQuestion, "id" | "type" | "options">,
  raw: string | undefined,
  answers?: Record<string, string>,
): string {
  const value = raw?.trim() ?? "";
  if (!value) return "";

  const otherText = answers?.[otherTextAnswerKey(question.id)];

  if (question.type === "checkboxes") {
    const selected = parseCheckboxAnswer(value);
    const labels = selected
      .map((id) =>
        formatChoiceOptionLabel(
          question.options?.find((option) => option.id === id),
          id,
          otherText,
        ),
      )
      .filter(Boolean);
    return labels.join(", ");
  }

  if (question.type === "multiple_choice") {
    return formatChoiceOptionLabel(
      question.options?.find((option) => option.id === value),
      value,
      otherText,
    );
  }

  if (question.type === "document_upload") {
    const parsed = parseDocumentUploadAnswer(value);
    if (parsed.fileId) {
      return parsed.fileName ? `Document: ${parsed.fileName}` : "Document uploaded";
    }
    return parsed.explanation ? capitalizeLeadingCharacter(parsed.explanation) : "";
  }

  return capitalizeLeadingCharacter(value);
}
